import ProductSpecificationFields from "../../components/ProductSpecificationFields";
import ProductSizeOptions from "../../components/ProductSizeOptions";
import { hydrateProductOptions, serialiseProductOptions } from "../../utils/productSizeOptions";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import DiscountFields, { discountPayloadOf } from "../../components/DiscountFields";
import { useProductFormGate } from "../../components/team/Gated";
import {
  MdAddAPhoto,
  MdWarning,
  MdArrowBack,
  MdEmail,
  MdCheckCircle,
  MdHourglassEmpty,
  MdInfo,
  MdSave,
  MdInventory2,
  MdAttachMoney,
  MdToggleOn,
} from "react-icons/md";
import { Switch, FormControlLabel } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { uploadImageToCloudinary as sharedUpload } from "../../utilities/imageUpload";
import { toast } from "react-toastify";
import { getTokenFromLocalStorage } from "../../utilities/tokenUtils.js";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { AppAsyncSelect as AsyncSelect } from "../../components/ui/AppReactSelect";
import CategorySuggestions from "../../components/catalog/CategorySuggestions";
import UnitOptions from "../../components/catalog/UnitOptions";
import { categoryPickerOptions } from "../../utilities/catalogTaxonomy";
import MobileProductShootPicker from "../../components/MobileProductShootPicker";
import AdditionalImagesStrip from "../../components/AdditionalImagesStrip";
import ProductMediaDeleteDialog from "../../components/ProductMediaDeleteDialog";
import ProductVideoSection from "../../components/ProductVideoSection";
import ArtworkTemplateLink from "../../components/ArtworkTemplateLink";
import UploadProgress from "../../components/UploadProgress";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import { productPhotoHint } from "../../utilities/productPhotoRules";
import ProductFreightFields, {
  normaliseProductFulfillment,
  serialiseProductFulfillment,
} from "../../components/ProductFreightFields";
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:5000";

const useStyles = makeStyles(() => ({
  label: {
    fontWeight: "normal",
    fontSize: "12px",
    textTransform: "capitalize",
  },
}));

// ── Design tokens (match brand colour palette) ────────────────────────────────
const BRAND_DARK  = "#17385a";
const BRAND_MID   = "#0a578b";
const BRAND_LIGHT = "#eff6ff";
const SUCCESS     = "#16a34a";
const WARNING_CLR = "#d97706";

const normaliseCategoryOption = (category, knownCategories = []) => {
  if (!category) return null;

  if (typeof category === "object") {
    const id = category._id || category.value;
    const label =
      category.category ||
      category.label ||
      knownCategories.find((item) => String(item._id) === String(id))?.category;

    return id && label ? { ...category, _id: id, category: label } : null;
  }

  const match = knownCategories.find((item) => String(item._id) === String(category));
  return match || null;
};

const normaliseCategorySelection = (categories, knownCategories = []) =>
  (Array.isArray(categories) ? categories : [])
    .map((category) => normaliseCategoryOption(category, knownCategories))
    .filter(Boolean);

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.ebpf-card {
  animation: ebpfFadeIn 0.4s ease-out;
  border: none !important;
  box-shadow: 0 8px 32px rgba(23,56,90,0.10) !important;
  border-radius: 18px !important;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
}

.ebpf-header {
  background: linear-gradient(135deg, ${BRAND_MID} 0%, ${BRAND_DARK} 100%) !important;
  color: white !important;
  padding: 28px 32px !important;
  border: none !important;
}

.ebpf-body {
  padding: 32px !important;
  background: #f8fafc;
}

.ebpf-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  margin-bottom: 20px;
  overflow: hidden;
}

.ebpf-section-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 11px 18px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ebpf-section-title {
  font-weight: 700;
  font-size: 0.80rem;
  color: #1a2f4e;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0;
}

.ebpf-section-body {
  padding: 20px 18px;
}

/* Number visible field labels in their rendered order, including optional fields. */
.ebpf-body form { counter-reset: ebpf-field; }
.ebpf-body .ebpf-label::before {
  counter-increment: ebpf-field;
  content: counter(ebpf-field) ". ";
  font-weight: 700;
}
.ebpf-description { margin-bottom: 24px; }
.ebpf-description .quill { height: auto; margin: 0; }
.ebpf-description .ql-toolbar { border-radius: 9px 9px 0 0; }
.ebpf-description .ql-container { height: auto; border-radius: 0 0 9px 9px; }
.ebpf-description .ql-editor { min-height: 180px; padding: 16px; font-size: 16px; line-height: 1.6; }
.ebpf-category { margin-top: 0; }
.ebpf-category .ebpf-label { margin-bottom: 10px !important; }
.ebpf-category .form-text { display: block; margin-top: 12px; line-height: 1.6; }

.ebpf-label {
  font-weight: 600 !important;
  color: ${BRAND_MID} !important;
  font-size: 13px !important;
  margin-bottom: 6px !important;
  display: block;
}

.ebpf-control {
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 9px !important;
  padding: 9px 13px !important;
  font-size: 13.5px !important;
  transition: border-color 0.2s, box-shadow 0.2s !important;
  background: #fff !important;
}

.ebpf-control:focus {
  border-color: ${BRAND_MID} !important;
  box-shadow: 0 0 0 3px rgba(10,87,139,0.12) !important;
}

.ebpf-btn-primary {
  background: ${BRAND_DARK} !important;
  border: none !important;
  color: white !important;
  font-weight: 700 !important;
  padding: 10px 26px !important;
  border-radius: 9px !important;
  transition: all 0.2s !important;
  font-size: 14px !important;
  box-shadow: 0 3px 10px rgba(23,56,90,0.25) !important;
}

.ebpf-btn-primary:hover:not(:disabled) {
  background: ${BRAND_MID} !important;
  transform: translateY(-1px);
  box-shadow: 0 5px 14px rgba(10,87,139,0.3) !important;
}

.ebpf-btn-secondary {
  background: #f1f5f9 !important;
  border: 1.5px solid #cbd5e1 !important;
  color: #475569 !important;
  font-weight: 600 !important;
  padding: 10px 22px !important;
  border-radius: 9px !important;
  transition: all 0.2s !important;
  font-size: 14px !important;
}

.ebpf-btn-secondary:hover:not(:disabled) {
  background: #e2e8f0 !important;
}

.ebpf-image-drop {
  border: 2px dashed ${BRAND_MID} !important;
  border-radius: 14px !important;
  background: ${BRAND_LIGHT} !important;
  transition: all 0.25s !important;
  padding: 28px;
  text-align: center;
}

.ebpf-image-drop:hover {
  border-color: ${BRAND_DARK} !important;
  background: #dbeafe !important;
}

.ebpf-ownership-banner {
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 20px;
}

.ebpf-price-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: 12px;
}

.price-tile {
  min-width: 0;
  width: 100%;
  text-align: left;
  font: inherit;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 13px 14px;
  cursor: pointer;
  background: #fff;
  transition: all 0.12s;
  user-select: none;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.price-tile:focus-visible { outline: 3px solid ${BRAND_MID}; outline-offset: 3px; }
.price-tile:disabled { cursor: not-allowed; opacity: 0.7; }
.price-tile > div:last-child { min-width: 0; }

.price-tile.active {
  border-color: ${BRAND_MID};
  background: ${BRAND_LIGHT};
}

.price-tile-icon {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
}

@media (max-width: 767px) {
  .ebpf-page { padding: 0 12px 24px !important; }
  .ebpf-page > .row { margin-left: 0; margin-right: 0; }
  .ebpf-page > .row > div { padding-left: 0; padding-right: 0; min-width: 0; }
  .ebpf-card { border-radius: 12px !important; }
  .ebpf-header { padding: 16px 12px !important; }
  .ebpf-heading { min-width: 0; overflow-wrap: anywhere; }
  .ebpf-back { flex-shrink: 0; min-width: 44px; min-height: 44px; }
  .ebpf-body { padding: 12px !important; }
  .ebpf-section { border-radius: 10px; margin-bottom: 12px; }
  .ebpf-section-header { padding: 12px; }
  .ebpf-section-body { padding: 14px 12px; }
  .ebpf-control { font-size: 16px !important; min-height: 44px; }
  .ebpf-image-drop { padding: 20px 12px; }
  .ebpf-cover { max-height: 220px !important; object-fit: contain; }
  .ebpf-image-actions { flex-wrap: wrap; }
  .ebpf-image-actions label, .ebpf-image-actions button {
    min-height: 44px; display: inline-flex; align-items: center;
  }
  .ebpf-template { margin-bottom: 12px; overflow-wrap: anywhere; }
  .ebpf-template a { min-height: 44px; }
  .ebpf-template svg { flex-shrink: 0; }
  .ebpf-ownership-banner { padding: 12px; gap: 10px; flex-wrap: wrap; overflow-wrap: anywhere; }
  .ebpf-ownership-banner > div:nth-child(2) { flex-basis: calc(100% - 40px) !important; }
  .ebpf-ownership-banner > span { width: 100%; }
  .ebpf-ownership-banner button { width: 100%; min-height: 44px; }
  .ebpf-price-options { grid-template-columns: 1fr; gap: 10px; }
  .ebpf-card .price-tile { padding: 14px; gap: 12px; border-radius: 12px; }
  .ebpf-actions { flex-direction: column-reverse; }
  .ebpf-actions button { width: 100%; min-height: 44px; }
  .ebpf-card .ql-toolbar { padding: 8px 4px; }
}

@keyframes ebpfFadeIn {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

// ── Sub-component: Section wrapper ────────────────────────────────────────────

const FormSection = ({ icon, title, children }) => (
  <div className="ebpf-section">
    <div className="ebpf-section-header">
      {icon}
      <span className="ebpf-section-title">{title}</span>
    </div>
    <div className="ebpf-section-body">{children}</div>
  </div>
);

// ── Sub-component: Ownership / Vendor Onboarding banner ──────────────────────

const OwnershipBanner = ({ brandOwner, brandId, disabled }) => {
  const [sending, setSending] = useState(false);

  const handleSendEmail = async () => {
    if (!brandOwner?._id) return;
    setSending(true);
    try {
      const tok = getTokenFromLocalStorage();
      await axios.post(
        `${API_URL}/api/sellers/resend-verification-email/${brandOwner._id}`,
        {},
        { headers: tok ? { Authorization: `Bearer ${tok}` } : {} }
      );
      toast.success(
        `Onboarding email sent to ${brandOwner.email}. They can now log in and manage their catalogue.`
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to send email. Please check the vendor is verified.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (!brandOwner) return null;

  const isVerified = brandOwner.sellerVerification === "VERIFIED";

  return (
    <div
      className="ebpf-ownership-banner"
      style={{
        background: isVerified ? "#f0fdf4" : "#fffbeb",
        border: `1.5px solid ${isVerified ? "#bbf7d0" : "#fde68a"}`,
      }}
    >
      <div style={{ flexShrink: 0, marginTop: 2 }}>
        {isVerified ? (
          <MdCheckCircle size={22} color={SUCCESS} />
        ) : (
          <MdHourglassEmpty size={22} color={WARNING_CLR} />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#1a2f4e", marginBottom: 4 }}>
          Brand owner:{" "}
          <span style={{ color: BRAND_MID }}>{brandOwner.name || brandOwner.email}</span>
        </div>
        <div style={{ fontSize: "0.80rem", color: "#475569", lineHeight: 1.5 }}>
          {brandOwner.email && (
            <>
              <strong>Email:</strong> {brandOwner.email}
            </>
          )}{" "}
          &nbsp;·&nbsp;{" "}
          <strong>Status:</strong>{" "}
          <Badge
            bg={isVerified ? "success" : "warning"}
            style={{ fontSize: "0.72rem" }}
          >
            {brandOwner.sellerVerification || "PENDING"}
          </Badge>
        </div>
        {!isVerified && (
          <div style={{ marginTop: 6, fontSize: "0.77rem", color: "#92400e" }}>
            This vendor account hasn&apos;t been verified yet. Must be Verified first via the Sellers list, then send the onboarding email.
          </div>
        )}
      </div>

      <OverlayTrigger
        placement="left"
        overlay={
          <Tooltip>
            {isVerified
              ? `Re-send the welcome & dashboard access email to ${brandOwner.email}`
              : "Vendor must be verified before you can send an onboarding email"}
          </Tooltip>
        }
      >
        <span>
          <Button
            size="sm"
            onClick={handleSendEmail}
            disabled={disabled || sending || !isVerified}
            style={{
              background: isVerified ? BRAND_DARK : "#d1d5db",
              borderColor: isVerified ? BRAND_DARK : "#d1d5db",
              color: "#fff",
              fontWeight: 700,
              borderRadius: 8,
              whiteSpace: "nowrap",
              fontSize: "0.80rem",
              padding: "7px 14px",
            }}
          >
            {sending ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Sending…
              </>
            ) : (
              <>
                <MdEmail size={15} style={{ marginRight: 5, marginTop: -2 }} />
                Send onboarding email
              </>
            )}
          </Button>
        </span>
      </OverlayTrigger>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

/**
 * The form's state for one product document, whichever endpoint it came from.
 *
 * This literal used to exist THREE times in this file (the initial state, the
 * router-state hydration and the fetched hydration), and a field added to one
 * and forgotten in another is exactly how a saved value fails to show on the
 * next open. One function, three callers.
 *
 * @param {object|null} src             a brand product as the API returns it
 * @param {string}      [fallbackBrandId] used when the document carries none
 */
const formStateFrom = (src, fallbackBrandId) => ({
    name: src?.name || "",
    description: src?.description || "",
    price: src?.price || 0.0,
    countInStock: src?.countInStock || 1,
    measurements: src?.measurements ?? 1,
    unit_id: src?.unit_id?._id || src?.unit_id || null,
    image: src?.image || null,
    /* Prefill any extra angles from the fetched product so the strip
       renders pre-populated tiles. The backend accepts a re-sent array
       of the same URLs as a no-op (URLs that survive the diff are kept
       verbatim; only removed ones get reclaimed from Cloudinary). */
    additionalImages: Array.isArray(src?.additionalImages)
      ? src.additionalImages
      : [],
    /* Product videos — frozen contract entries, see ProductVideoSection.
       Hydrated here (and in BOTH hydration passes below) so a save can
       never destroy stored videos the way early resets destroyed
       additionalImages. */
    videos: Array.isArray(src?.videos) ? src.videos : [],
    enabled: src?.enabled !== undefined ? src.enabled : true,
    sectionId: src?.sectionId || "",
    brandId: src?.brandId || fallbackBrandId || "",
    category: Array.isArray(src?.category) ? src.category : [],
    skuCode: src?.skuCode || "",
    priceOnRequest: src?.priceOnRequest || false,
    tierPrices: {
      retailer: src?.tierPrices?.retailer ?? "",
      wholesaler: src?.tierPrices?.wholesaler ?? "",
    },
    orderFlowOverride: src?.orderFlowOverride || "INHERIT",
    requiredPaymentMethod: src?.requiredPaymentMethod || "NONE",
    discount: src?.discount || null,
    minOrderQty: src?.minOrderQty || 1,
    leadTimeDays: src?.leadTimeDays ?? "",
    fulfillment: normaliseProductFulfillment(src?.fulfillment),
    // Product-owned specifications and customer choices.
    specs: src?.specs || {},
    specOptions: hydrateProductOptions(src?.specOptions, src?.price),
});

const EditBrandProductForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { productId } = useParams();

  const productDetails = location.state?.productDetails;
  const brandName = location.state?.brandName || "Brand";
  const isMounted = useRef(true);
  const brandIdFromState = location.state?.brandId || productDetails?.brandId;

  const currentUser = getUserFromLocalStorage()?.result;
  const vendorId = currentUser?._id;

  // ── State ──────────────────────────────────────────────────────────────────

  const [loading, setLoading] = useState(!productDetails);
  const [brandOwner, setBrandOwner] = useState(null); // { _id, name, email, sellerVerification }

  /* siUnits for the measurement dropdown, fetched the same relative way this
     form already fetches categories. `measurements` is a bare number on the
     product; without its unit the storefront renders "Measurements: 1", which
     is what every brand product in the database currently shows — neither
     field was on this form at all. */
  const [units, setUnits] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/unit");
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) setUnits(data);
      } catch (err) {
        // Non-fatal — the product can still be saved without a unit.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Saved photo (https URL) whose X was clicked; opens ProductMediaDeleteDialog.
  const [photoDeleteTarget, setPhotoDeleteTarget] = useState(null);
  const [product, setProduct] = useState(() =>
    formStateFrom(productDetails, brandIdFromState)
  );
  /* The last state this form was HYDRATED to (as opposed to edited into).
     The server's copy arrives a moment after the router's; it replaces the
     form only while the form still IS the hydrated object, i.e. nobody has
     typed yet. Object identity is the dirty flag: every edit builds a new
     object, so `product !== hydratedRef.current` means "being edited". */
  const hydratedRef = useRef(null);

  const [priceWarning, setPriceWarning] = useState("");
  const [imagePreview, setImagePreview] = useState(productDetails?.image || null);
  /* When the form is REPLACED by a hydration (the server's copy landing after
     the router's), the cover preview has to follow it. An edit sets the
     preview itself, and an edit is exactly a `product` that is not the
     hydrated object, so this effect is a no-op for every keystroke. */
  useEffect(() => {
    if (hydratedRef.current && product === hydratedRef.current) {
      setImagePreview(product.image || null);
    }
  }, [product]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(
    normaliseCategorySelection(productDetails?.category || [])
  );

  const busy = submitting || uploading;
  const gate = useProductFormGate(busy);

  // ── Fetch product + brand owner ────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      const effectiveBrandId = brandIdFromState || productDetails?.brandId;

      // ── Fetch brand owner info (always, regardless of whether we have productDetails)
      if (effectiveBrandId) {
        try {
          const tok = getTokenFromLocalStorage();
          const brandRes = await axios.get(
            `${API_URL}/api/brands/${effectiveBrandId}`,
            { headers: tok ? { Authorization: `Bearer ${tok}` } : {} }
          );
          const brand = brandRes.data?.data;
          if (brand?.owner) {
            const ownerObj = brand.owner;
            const sellerRes = await axios.get(
              `${API_URL}/api/sellers/${ownerObj._id}`,
              { headers: tok ? { Authorization: `Bearer ${tok}` } : {} }
            ).catch(() => null);

            const sellerData = sellerRes?.data?.seller || sellerRes?.data?.data;
            setBrandOwner({
              _id: ownerObj._id,
              name: ownerObj.name || sellerData?.name,
              email: ownerObj.email || sellerData?.email,
              sellerVerification: sellerData?.sellerVerification || "PENDING",
            });
          }
        } catch (err) {
          // Non-fatal — ownership banner simply won't show
          console.warn("[EditBrandProductForm] Could not fetch brand owner:", err?.message);
        }
      }

      // ── Hydrate product form ──────────────────────────────────────────────

      /* The sections aggregation used to omit `videos`. Treat a navigation
         object without that key as an incomplete cache entry and fall through
         to the authoritative single-product endpoint. Hydrating it as [] is
         destructive: the next unrelated save would tell the server to remove
         the real stored videos and its cleanup would delete those assets. */
      const productDetailsAreMediaComplete =
        productDetails &&
        Object.prototype.hasOwnProperty.call(productDetails, "videos");

      /* Paint from the router's copy at once, but DO NOT stop there. That copy
         is whatever the list page held when the row was clicked, and it is
         wrong in two ordinary cases: the browser Back button after a save
         (the history entry keeps the pre-save state), and a list fetched
         before a save made in another tab. Both read as "my change did not
         persist" when the only thing that had not persisted was the copy in
         the URL bar. The single-product endpoint below is the authority and
         always runs; it replaces the form only while nothing has been typed. */
      const paintedFromRouter = productDetailsAreMediaComplete && !!effectiveBrandId;
      if (paintedFromRouter) {
        const first = formStateFrom(productDetails, effectiveBrandId);
        hydratedRef.current = first;
        setProduct(first);
        setImagePreview(productDetails.image || null);
        setLoading(false);
      }

      if (!productId) {
        toast.error("Product ID is missing. Redirecting...");
        history.push("/admin/brand-product-management");
        return;
      }

      if (!effectiveBrandId) {
        toast.error("Brand ID is missing. Redirecting...");
        history.push("/admin/brands-management");
        return;
      }

      // The authoritative copy. The only source when there was no router copy.
      try {
        const tok = getTokenFromLocalStorage();
        const resp = await axios.get(
          `${API_URL}/api/brands/${effectiveBrandId}/products/${productId}`,
          { headers: tok ? { Authorization: `Bearer ${tok}` } : {} }
        );
        if (resp.data.success && resp.data.data && isMounted.current) {
          const fresh = formStateFrom(resp.data.data, effectiveBrandId);
          setProduct((current) => {
            // Already being edited: the operator's keystrokes outrank a refetch.
            if (hydratedRef.current && current !== hydratedRef.current) return current;
            hydratedRef.current = fresh;
            return fresh;
          });
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        /* With the router's copy already on screen a failed refetch is a
           degraded open, not a dead one: leave the form, say nothing. */
        if (!paintedFromRouter) {
          toast.error("Failed to load product details. Redirecting...");
          history.push("/admin/brands-management");
        }
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    load();

    return () => { isMounted.current = false; };
  }, [productId]); // load() is defined inside the effect; productId is the only external dep

  const fetchCategoryOptions = useCallback(async () => {
    try {
      const res = await fetch("/category/get-all");
      const categories = await res.json();
      return Array.isArray(categories) ? categories : [];
    } catch (error) {
      console.error("Error loading categories:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const categories = await fetchCategoryOptions();
      if (!cancelled && isMounted.current) {
        setCategoryOptions(categories);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchCategoryOptions]);

  useEffect(() => {
    setSelectedCategory((current) => {
      if (current.length) return normaliseCategorySelection(current, categoryOptions);
      return normaliseCategorySelection(productDetails?.category || product.category || [], categoryOptions);
    });
  }, [categoryOptions, product.category, productDetails?.category]);

  const loadCategory = useCallback(
    async (inputValue) => {
      const categories = await fetchCategoryOptions();
      // Grouped, "All"/"Random"/inactive left out (unless already chosen),
      // aliases and keywords searchable.
      return categoryPickerOptions(categories, { input: inputValue, selected: selectedCategory });
    },
    [fetchCategoryOptions, selectedCategory]
  );

  const handleCategoryChange = (value) => {
    setSelectedCategory(normaliseCategorySelection(value, categoryOptions));
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "price") {
      if (parseFloat(value) < 0) {
        setPriceWarning("Price must be 0 or higher.");
        setProduct((p) => ({ ...p, [name]: "" }));
        return;
      }
      setPriceWarning("");
    }
    if (name === "countInStock" && value < 1) {
      toast.error("Stock cannot be less than 1");
      return;
    }
    setProduct((p) => ({ ...p, [name]: value }));
  };

  const handleTierChange = (tier, value) =>
    setProduct((p) => ({ ...p, tierPrices: { ...p.tierPrices, [tier]: value } }));

  /* Ignore ReactQuill's own re-render echoes (source "api"); only typing
     (source "user") is a description change. See the create form. */
  const handleDescriptionChange = (value, _delta, source) => {
    if (source && source !== "user") return;
    setProduct((p) => ({ ...p, description: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    // Clearing the input lets the SAME file be picked twice in a row —
    // otherwise no change event fires and the second attempt looks dead.
    e.target.value = "";
    if (!file) { setUploadError("No file selected."); return; }
    if (!file.type.startsWith("image/")) { setUploadError("Please select an image file."); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadError("File must be under 10 MB."); return; }
    setUploadError(null);
    const reader = new FileReader();
    reader.onload = () => {
      if (!isMounted.current) return;
      setImagePreview(reader.result);
      setProduct((p) => ({ ...p, image: reader.result }));
    };
    reader.onerror = () => {
      if (!isMounted.current) return;
      setUploadError("Failed to read image.");
      setImagePreview(null);
      setProduct((p) => ({ ...p, image: null }));
    };
    reader.readAsDataURL(file);
  };

  const uploadImageToCloudinary = async (imageData, assetPurpose = "product_cover") => {
    if (!imageData) return null;
    if (typeof imageData === "string" && !imageData.startsWith("data:")) return imageData;
    setUploading(true);
    try {
      const result = await sharedUpload(imageData, {
        skipValidation: true,
        vendorMedia: {
          vendorType: "brand",
          vendorId: product.brandId,
          assetPurpose,
          entityType: "brandProduct",
          entityId: productId,
          source: "dashboard",
          pendingAttachment: true,
        },
        onProgress: (pct) => { if (isMounted.current) setUploadProgress(pct); },
      });
      if (!isMounted.current) return null;
      setUploading(false);
      return result.secure_url;
    } catch (err) {
      if (!isMounted.current) return null;
      toast.error(`Image upload failed: ${err.message}`);
      setUploading(false);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let optionsPayload;
    try {
      optionsPayload = serialiseProductOptions(product.specOptions, product.price, product.priceOnRequest);
    } catch (error) {
      toast.error(error.message);
      return;
    }

    setSubmitting(true);
    setUploading(true);

    let imageUrl = product.image;
    if (imageUrl && typeof imageUrl === "string" && imageUrl.startsWith("data:")) {
      imageUrl = await uploadImageToCloudinary(imageUrl);
      if (!imageUrl) {
        if (isMounted.current) { toast.error("Image upload failed, please try again."); setUploading(false); setSubmitting(false); }
        return;
      }
    } else if (!imageUrl) {
      if (isMounted.current) { toast.error("Please add a product image."); setUploading(false); setSubmitting(false); }
      return;
    }

    /* Resolve newly-picked extra angles. Already-uploaded https URLs
       pass through; dataURLs go through Cloudinary. The backend then
       diffs the resolved list against the stored `additionalImages`
       and reclaims any URL that no longer appears. */
    let resolvedAdditional = [];
    const picked = Array.isArray(product.additionalImages)
      ? product.additionalImages
      : [];
    for (const entry of picked) {
      if (typeof entry !== "string" || !entry) continue;
      if (entry.startsWith("data:")) {
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadImageToCloudinary(entry, "product_angle");
        if (!url) {
          if (isMounted.current) {
            toast.error("One of the extra angles failed to upload.");
            setUploading(false);
            setSubmitting(false);
          }
          return;
        }
        resolvedAdditional.push(url);
      } else {
        resolvedAdditional.push(entry);
      }
    }

    /* Collected only for a product that publishes a price. A vendor who typed
       tier figures and then switched to "Price on request" had them submitted
       anyway, so the stored product contradicted itself: priceOnRequest true
       with a retailer tier beside it. */
    const tierPrices = {};
    if (!product.priceOnRequest) {
      if (product.tierPrices.retailer !== "") tierPrices.retailer = parseFloat(product.tierPrices.retailer);
      if (product.tierPrices.wholesaler !== "") tierPrices.wholesaler = parseFloat(product.tierPrices.wholesaler);
    }

    const payload = {
      name: product.name,
      description: product.description,
      price: product.priceOnRequest ? 0 : parseFloat(product.price) || 0,
      image: imageUrl,
      additionalImages: resolvedAdditional,
      /* Hydrated on load and replaced wholesale on save — contract-shaped
         entries, uploaded on pick by ProductVideoSection. */
      videos: Array.isArray(product.videos) ? product.videos : [],
      countInStock: parseInt(product.countInStock) || 1,
      measurements: Number(product.measurements) || 1,
      // null, not undefined: the controller treats undefined as "leave alone",
      // so clearing the dropdown has to send an explicit null.
      unit_id: product.unit_id || null,
      enabled: product.enabled,
      sectionId: product.sectionId,
      brandId: product.brandId,
      skuCode: product.skuCode || undefined,
      priceOnRequest: product.priceOnRequest,
      /* An empty object, NOT undefined, when the price is on request. The
         controller treats an absent `tierPrices` as "leave alone", so omitting
         it on an edit would keep whatever tiers the product was already
         carrying and the contradiction would survive the save that was meant
         to end it. Sending `{}` clears them. On the create path there is
         nothing stored yet, so that form omits the key instead. */
      tierPrices: product.priceOnRequest
        ? {}
        : Object.keys(tierPrices).length
          ? tierPrices
          : undefined,
      orderFlowOverride: product.orderFlowOverride,
      requiredPaymentMethod: product.requiredPaymentMethod,
      discount: discountPayloadOf(product.discount),
      minOrderQty: parseInt(product.minOrderQty) || 1,
      leadTimeDays: product.leadTimeDays !== "" ? parseInt(product.leadTimeDays) : undefined,
      category: selectedCategory.map((category) => category._id),
      fulfillment: serialiseProductFulfillment(product.fulfillment),
      /* Per-brand specs. Empty values are allowed (they clear the
         dictionary); shape validation happens server-side. */
      specs: product.specs && Object.keys(product.specs).length ? product.specs : {},
      specOptions: optionsPayload,
    };

    try {
      const tok = getTokenFromLocalStorage();
      await axios.put(
        `${API_URL}/api/brands/${product.brandId}/products/${productId}`,
        payload,
        { headers: tok ? { Authorization: `Bearer ${tok}` } : {} }
      );
      if (isMounted.current) {
        toast.success("Product updated successfully!");
        history.push("/admin/brand-product-management", { brandId: product.brandId, brandName });
      }
    } catch (err) {
      if (isMounted.current) {
        toast.error(err.response?.data?.message || "Failed to update product, please try again.");
        setSubmitting(false);
      }
    } finally {
      if (isMounted.current) { setUploading(false); setSubmitting(false); }
    }
  };

  // ── Guard renders ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="text-center">
          <Spinner animation="border" style={{ color: BRAND_MID }} />
          <p style={{ marginTop: 12, color: "#64748b", fontSize: "0.85rem" }}>Loading product…</p>
        </div>
      </div>
    );
  }

  if (!product.brandId || !productId) {
    return (
      <div className="content">
        <Alert variant="warning">Missing product information. Please select a product to edit.</Alert>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Container className="ebpf-page" style={{ paddingBottom: 40 }}>
      <style>{styles}</style>

      <Row className="justify-content-center">
        <Col md={10} lg={9}>

          {/* ── Ownership / Onboarding banner ──────────────────────── */}
          <OwnershipBanner
            brandOwner={brandOwner}
            brandId={product.brandId}
            disabled={gate.lockCatalogue} title={gate.catalogueTitle}
          />

          {/* ── Main card ─────────────────────────────────────────── */}
          <Card className="ebpf-card">

            {/* Header */}
            <Card.Header className="ebpf-header">
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant="link"
                  className="ebpf-back"
                  aria-label="Back to products"
                  onClick={() => history.push("/admin/brand-product-management", { brandId: product.brandId, brandName })}
                  style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.4rem", padding: 0 }}
                >
                  <MdArrowBack />
                </Button>
                <div className="ebpf-heading">
                  <Card.Title as="h3" style={{ margin: 0, color: "#fff", fontSize: "1.25rem", fontWeight: 700 }}>
                    Edit Product
                  </Card.Title>
                  <p style={{ margin: "5px 0 0", opacity: 0.80, fontSize: "0.85rem" }}>
                    {brandName}
                    {product.name ? ` · ${product.name}` : ""}
                  </p>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="ebpf-body">
              {/* Vendor team permissions: what this login may change here. */}
              {gate.pricingOnly && (
                <Alert variant="info" className="mb-3">
                  Your role allows price changes only. Every other field is locked; Save will update the pricing fields.
                </Alert>
              )}
              {gate.readOnly && (
                <Alert variant="warning" className="mb-3">
                  {gate.readOnlyMessage}
                </Alert>
              )}
              <Form onSubmit={handleSubmit}>

                {/* ── 1. Product Image ─────────────────────────────── */}
                <FormSection
                  icon={<MdAddAPhoto size={16} color={BRAND_MID} />}
                  title="Product Image"
                >
                  {/* The artwork template for this slot, when one is configured. */}
                  <ArtworkTemplateLink slotKey="brand.productCover" variant="inline" className="ebpf-template" />
                  {!product.image ? (
                    <div className="ebpf-image-drop">
                      <Form.Control
                        type="file"
                        accept=".jpg,.png,.jpeg,.webp"
                        onChange={handleImageChange}
                        disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        className="d-none"
                        id="image-upload"
                      />
                      <Form.Label
                        htmlFor="image-upload"
                        style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, margin: 0 }}
                      >
                        <MdAddAPhoto size={44} style={{ color: BRAND_MID }} />
                        <span style={{ fontWeight: 700, color: BRAND_MID, fontSize: "0.95rem" }}>Click to upload</span>
                        <span style={{ fontSize: "0.78rem", color: "#64748b" }}>{productPhotoHint("JPG, PNG or WebP")}</span>
                      </Form.Label>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <img
                        src={imagePreview || product.image}
                        alt="Preview"
                        className="ebpf-cover"
                        style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 10, marginBottom: 12, border: "1px solid #e2e8f0" }}
                      />
                      <div className="ebpf-image-actions d-flex justify-content-center gap-3">
                        <Form.Control
                          type="file"
                          accept=".jpg,.png,.jpeg,.webp"
                          onChange={handleImageChange}
                          disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                          className="d-none"
                          id="image-replace"
                        />
                        <Form.Label
                          htmlFor="image-replace"
                          style={{
                            color: busy ? "#94a3b8" : SUCCESS,
                            textDecoration: "none",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            cursor: busy ? "not-allowed" : "pointer",
                            marginBottom: 0,
                            alignSelf: "center",
                            pointerEvents: busy ? "none" : "auto",
                          }}
                        >
                          Replace
                        </Form.Label>
                        <span style={{ color: "#cbd5e1", alignSelf: "center" }}>|</span>
                        <Button
                          variant="link"
                          style={{ color: "#ef4444", textDecoration: "none", fontWeight: 600, fontSize: "0.85rem" }}
                          onClick={() => { setProduct((p) => ({ ...p, image: null })); setImagePreview(null); }}
                          disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}

                  {uploadError && (
                    <Alert variant="danger" className="mt-2 mb-0" style={{ fontSize: "0.82rem" }}>{uploadError}</Alert>
                  )}
                  {uploading && (
                    <UploadProgress percent={uploadProgress} label="Uploading image" />
                  )}

                  {/* Mobile shoot picker ── already built, just rendered here */}
                  <MobileProductShootPicker
                    vendorId={vendorId}
                    draftId={productId}
                    disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    onSelectImage={(imageUrl) => {
                      setProduct((p) => ({ ...p, image: imageUrl }));
                      setImagePreview(imageUrl);
                      setUploadError(null);
                    }}
                  />
                  <AdditionalImagesStrip
                    value={product.additionalImages}
                    onChange={(next) =>
                      setProduct((p) => ({ ...p, additionalImages: next }))
                    }
                    onRequestDeleteSaved={setPhotoDeleteTarget}
                    disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                  />
                  <ProductVideoSection
                    videos={product.videos || []}
                    onChange={(next) =>
                      setProduct((p) => ({ ...p, videos: next }))
                    }
                    disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                  />
                </FormSection>

                {/* ── 2. Product Identity ──────────────────────────── */}
                <FormSection
                  icon={<MdInfo size={16} color={BRAND_MID} />}
                  title="Product Identity"
                >
                  <Form.Group className="mb-3">
                    <label className="ebpf-label">Product Name *</label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={product.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. Premium Kraft Boxes – A4 500ml"
                      disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                      className="ebpf-control"
                    />
                  </Form.Group>
                  <Form.Group className="ebpf-description">
                    <label className="ebpf-label">Description <span style={{ fontWeight: 400, color: "#94a3b8" }}>(optional)</span></label>
                    <ReactQuill
                      theme="snow"
                      value={product.description}
                      onChange={handleDescriptionChange}
                      placeholder="Describe materials, sizes, use cases…"
                      readOnly={busy}
                    />
                  </Form.Group>
                  <Form.Group className="ebpf-category">
                    <label className="ebpf-label">Category Association</label>
                    {/* The menu has to ESCAPE the card, not out-rank it.
                        `.ebpf-section` sets `overflow: hidden` (to keep content
                        inside its 14px radius), and an ancestor's overflow CLIPS
                        its descendants — that is a layout decision, not a paint
                        order one, so no z-index on the menu can ever get it out.
                        `menuPortalTarget` re-parents the menu to <body>, outside
                        the clipping ancestor entirely. Only THEN does z-index
                        matter, and it is needed there because the portalled menu
                        now has to beat Bootstrap's overlay stack (modal 1050,
                        popover 1060, tooltip 1070) — react-select's own default
                        for menuPortal is z-index 1.
                        `menuPosition="fixed"` keeps the menu pinned to the
                        control when the page scrolls, which a body-portalled
                        absolute menu otherwise drifts away from. */}
                    <AsyncSelect
                      defaultOptions={categoryPickerOptions(categoryOptions, { selected: selectedCategory })}
                      value={selectedCategory}
                      getOptionLabel={(option) => option.category}
                      getOptionValue={(option) => option._id}
                      loadOptions={loadCategory}
                      onChange={handleCategoryChange}
                      isClearable
                      isMulti
                      isDisabled={busy}
                      placeholder="Search and select categories..."
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 1100 }),
                      }}
                    />
                    <CategorySuggestions
                      name={product.name}
                      unitId={product.unit_id}
                      vendorId={product.brandId}
                      categories={categoryOptions}
                      selected={selectedCategory}
                      disabled={busy}
                      onAdd={(category) =>
                        setSelectedCategory((prev) =>
                          normaliseCategorySelection([...prev, category], categoryOptions)
                        )
                      }
                    />
                    <Form.Text className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Used by the public shop to group this product with matching catalogue categories.
                    </Form.Text>
                  </Form.Group>
                </FormSection>

                {/* ── 3. Pricing ───────────────────────────────────── */}
                <FormSection
                  icon={<MdAttachMoney size={16} color={BRAND_MID} />}
                  title="Pricing"
                >
                  <p style={{ fontSize: "0.78rem", color: "#475569", marginBottom: 12, lineHeight: 1.6 }}>
                    Choose how buyers see this product’s price.
                  </p>

                  {/* Price type tiles */}
                  <div className="ebpf-price-options mb-3" role="group" aria-label="Pricing type">
                    {[
                      {
                        val: false,
                        label: "Fixed price",
                        icon: "fas fa-dollar-sign",
                        desc: "Show a UGX price, with optional buyer tiers below.",
                      },
                      {
                        val: true,
                        label: "Price on request",
                        icon: "fas fa-comments-dollar",
                        desc: "Let buyers contact you for a quote.",
                      },
                    ].map(({ val, label, icon, desc }) => {
                      const active = product.priceOnRequest === val;
                      return (
                        <button
                          type="button"
                          aria-pressed={active}
                          disabled={gate.lockPricing}
                          key={String(val)}
                          className={`price-tile${active ? " active" : ""}`}
                          title={gate.pricingTitle}
                          onClick={() => !gate.lockPricing && setProduct((p) => ({ ...p, priceOnRequest: val }))}
                        >
                          <div
                            className="price-tile-icon"
                            style={{ background: active ? BRAND_MID : "#f1f5f9" }}
                          >
                            <i className={icon} style={{ fontSize: "0.8rem", color: active ? "#fff" : "#94a3b8" }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: "0.82rem", color: active ? BRAND_MID : "#334155" }}>{label}</div>
                            <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: 2 }}>{desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {!product.priceOnRequest && (
                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <label className="ebpf-label">Base Price (UGX) *</label>
                        <Form.Control
                          type="number"
                          name="price"
                          value={product.price}
                          onChange={handleInputChange}
                          required
                          min="0"
                          step="1"
                          placeholder="e.g. 12500"
                          disabled={gate.lockPricing} title={gate.pricingTitle}
                          className="ebpf-control"
                        />
                        <Form.Text className="text-muted" style={{ fontSize: "0.75rem" }}>
                          Default price shown to all buyers. Anonymous visitors always see this.
                        </Form.Text>
                        {priceWarning && (
                          <Alert variant="danger" className="mt-2 mb-0 py-1" style={{ fontSize: "0.78rem" }}>
                            <MdWarning size={15} className="me-1" />{priceWarning}
                          </Alert>
                        )}
                      </Col>
                    </Row>
                  )}

                  {/* Tier pricing. A per-buyer-type figure only means something
                      when there IS a published price to override, so the whole
                      block goes away once the vendor chooses "Price on request".
                      It used to stay on screen and keep submitting, which let one
                      SKU be saved saying both "ask us for a price" and "retailers
                      pay 12,500", and told the vendor two different things at once
                      while they filled the form. The submit handler clears the
                      stored tiers to match. */}
                  {!product.priceOnRequest && (
                    <div style={{ background: "#f0f7ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "14px 16px" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.78rem", color: BRAND_MID, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                        <i className="fas fa-layer-group" style={{ fontSize: "0.75rem" }} />
                        Tier Pricing
                        <span style={{ fontWeight: 400, color: "#64748b", fontSize: "0.72rem" }}>optional override per buyer type</span>
                      </div>
                      <p style={{ fontSize: "0.72rem", color: "#475569", marginBottom: 10, lineHeight: 1.5 }}>
                        Only applies when the brand is set to <strong>Tiered</strong> pricing. Signed-in buyers see their matching tier; anonymous visitors see the base price.
                      </p>
                      <Row className="g-2">
                        {[
                          ["retailer", "Retailer price", "Buyers who purchase individual units or small quantities to resell in their own shop."],
                          ["wholesaler", "Wholesaler price", "Buyers who purchase in bulk, typically to resell or distribute at scale."],
                        ].map(([tier, lbl, hint]) => (
                          <Col md={6} key={tier}>
                            <label className="small mb-1 fw-semibold" style={{ color: "#374151", display: "block" }}>{lbl}</label>
                            <Form.Control
                              size="sm"
                              type="number"
                              min="0"
                              step="1"
                              placeholder="Leave blank to use base price"
                              value={product.tierPrices[tier]}
                              onChange={(e) => handleTierChange(tier, e.target.value)}
                              disabled={gate.lockPricing} title={gate.pricingTitle}
                              className="ebpf-control"
                            />
                            <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontStyle: "italic", marginTop: 3 }}>{hint}</div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </FormSection>

                {/* ── 4. Inventory & Fulfilment ────────────────────── */}
                <FormSection
                  icon={<MdInventory2 size={16} color="#057a55" />}
                  title="Inventory & Fulfilment"
                >
                  {/* Measurement + unit. Stored as a number plus an siUnits ref and
                      rendered together by the clients; a number on its own tells a
                      buyer nothing. */}
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="ebpf-label">Measurement</Form.Label>
                        <Form.Control
                          type="number"
                          name="measurements"
                          min="0"
                          step="any"
                          value={product.measurements}
                          onChange={handleInputChange}
                          placeholder="e.g. 1000"
                          disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                          className="ebpf-control"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="ebpf-label">Unit</Form.Label>
                        <AppSelect block
                          value={product.unit_id || ""}
                          onChange={(e) =>
                            setProduct((prev) => ({ ...prev, unit_id: e.target.value || null }))
                          }
                          disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                          className="ebpf-control"
                        >
                          <option value="">No unit</option>
                          <UnitOptions units={units} currentId={product.unit_id} />
                        </AppSelect>
                        <Form.Text className="text-muted" style={{ fontSize: "0.75rem" }}>
                          Buyers see “{product.measurements || 1}{" "}
                          {units.find((u) => String(u._id) === String(product.unit_id))?.unit ||
                            "(no unit)"}
                          ” on the product page.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mb-3">
                    <Col md={4}>
                      <label className="ebpf-label">Count in Stock *</label>
                      <Form.Control
                        type="number"
                        name="countInStock"
                        value={product.countInStock}
                        onChange={handleInputChange}
                        required
                        min="1"
                        placeholder="e.g. 50"
                        disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        className="ebpf-control"
                      />
                      <Form.Text className="text-muted" style={{ fontSize: "0.75rem" }}>
                        Units available. Buyers can&apos;t order more than this.
                      </Form.Text>
                    </Col>
                    <Col md={4}>
                      <label className="ebpf-label">Min. Order Qty (MOQ)</label>
                      <Form.Control
                        type="number"
                        name="minOrderQty"
                        value={product.minOrderQty}
                        onChange={handleInputChange}
                        min="1"
                        placeholder="e.g. 10"
                        disabled={gate.lockPricing} title={gate.pricingTitle}
                        className="ebpf-control"
                      />
                      <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontStyle: "italic", marginTop: 3 }}>
                        The smallest number of units a buyer must order at once.
                      </div>
                    </Col>
                    <Col md={4}>
                      <label className="ebpf-label">Lead Time (days)</label>
                      <Form.Control
                        type="number"
                        name="leadTimeDays"
                        value={product.leadTimeDays}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="e.g. 3"
                        disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        className="ebpf-control"
                      />
                      <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontStyle: "italic", marginTop: 3 }}>
                        How many days it typically takes to prepare and dispatch this product after an order is placed.
                      </div>
                    </Col>
                  </Row>

                  <hr />
                  <ProductFreightFields
                    product={product}
                    setProduct={setProduct}
                    disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    labelClassName="ebpf-label"
                    controlClassName="ebpf-control"
                  />

                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <label className="ebpf-label">SKU Code</label>
                      <Form.Control
                        name="skuCode"
                        value={product.skuCode}
                        onChange={handleInputChange}
                        placeholder="e.g. PRM-BOX-A4-500"
                        disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        className="ebpf-control"
                      />
                      <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontStyle: "italic", marginTop: 3 }}>
                        Auto-generated unique code used to track this product in orders and inventory. You can edit it.
                      </div>
                    </Col>
                    <Col md={6}>
                      <label className="ebpf-label">Order Flow Override</label>
                      <AppSelect block
                        name="orderFlowOverride"
                        value={product.orderFlowOverride}
                        onChange={handleInputChange}
                        disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        className="ebpf-control"
                      >
                        <option value="INHERIT">Inherit from brand settings</option>
                        <option value="STANDARD">Standard catalogue</option>
                        <option value="BRANDED_PACKAGING">Branded Packaging</option>
                        <option value="QUOTE">Custom Quote only</option>
                      </AppSelect>
                      <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontStyle: "italic", marginTop: 3 }}>
                        Controls how buyers complete a purchase — e.g. standard checkout, requesting a custom quote, or ordering with branded packaging. Leave as "Inherit" to use the brand's default setting.
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-3">
                    <Col md={12}>
                      <label className="ebpf-label">Prepayment Required</label>
                      <AppSelect block
                        name="requiredPaymentMethod"
                        value={product.requiredPaymentMethod}
                        onChange={handleInputChange}
                        disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        className="ebpf-control"
                      >
                        <option value="NONE">No prepayment required (COD allowed)</option>
                        <option value="ANY_ONLINE">Any online payment before acceptance</option>
                        <option value="MOMO">MTN Mobile Money only</option>
                        <option value="AIRTEL">Airtel Money only</option>
                        <option value="VISA">Credit / Debit Card only</option>
                        <option value="ZAMTEL">Zamtel Kwacha only</option>
                      </AppSelect>
                      <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontStyle: "italic", marginTop: 3 }}>
                        Prepaid orders cannot be accepted or prepared until Lusuku confirms payment.
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-3 mt-1">
                    <Col md={12}>
                      {/* Vendor discount: the list price above stays; the sale price is derived on read. */}
                      <DiscountFields
                        disabled={gate.lockPricing}
                        title={gate.pricingTitle}
                        value={product.discount}
                        onChange={(next) => setProduct((prev) => ({ ...prev, discount: next }))}
                        listPrice={product.price}
                      />
                    </Col>
                  </Row>
                </FormSection>

                <ProductSizeOptions product={product} setProduct={setProduct}
                  catalogueDisabled={gate.lockCatalogue} pricingDisabled={gate.lockPricing} />
                <ProductSpecificationFields product={product} setProduct={setProduct} disabled={gate.lockCatalogue} />

                {/* ── 5. Visibility ────────────────────────────────── */}
                <FormSection
                  icon={<MdToggleOn size={16} color={BRAND_MID} />}
                  title="Visibility"
                >
                  <div className="d-flex align-items-center justify-content-between" style={{ flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={product.enabled}
                            onChange={(e) => setProduct((p) => ({ ...p, enabled: e.target.checked }))}
                            disabled={gate.lockActivation} title={gate.activationTitle}
                            color="primary"
                          />
                        }
                        label={
                          <span style={{ fontWeight: 600, fontSize: "0.88rem", color: "#1a2f4e" }}>
                            {product.enabled ? "Product is live" : "Product is hidden"}
                          </span>
                        }
                        className={classes.label}
                      />
                      <p style={{ margin: "2px 0 0 44px", fontSize: "0.78rem", color: "#64748b" }}>
                        {product.enabled
                          ? "Customers can see and order this product."
                          : "Disabled products are hidden from all customers."}
                      </p>
                    </div>
                    <Badge
                      bg={product.enabled ? "success" : "secondary"}
                      style={{ fontSize: "0.78rem", padding: "6px 12px" }}
                    >
                      {product.enabled ? "● Live" : "○ Hidden"}
                    </Badge>
                  </div>
                </FormSection>

                {/* ── Action buttons ───────────────────────────────── */}
                <div className="ebpf-actions d-flex justify-content-end gap-2 mt-4">
                  <Button
                    className="ebpf-btn-secondary"
                    onClick={() => history.push("/admin/brand-product-management", { brandId: product.brandId, brandName })}
                    disabled={busy}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="ebpf-btn-primary"
                    disabled={gate.lockSubmit} title={gate.submitTitle}
                  >
                    {busy ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {uploading ? "Uploading…" : "Saving…"}
                      </>
                    ) : (
                      <>
                        <MdSave size={16} style={{ marginRight: 6, marginTop: -2 }} />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>

              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ProductMediaDeleteDialog
        target={photoDeleteTarget}
        productType="brand"
        productId={productId}
        brandId={product?.brandId || brandIdFromState}
        productName={product?.name}
        image={product?.image}
        additionalImages={product?.additionalImages}
        onClose={() => setPhotoDeleteTarget(null)}
        onRemoved={({ image, additionalImages }) =>
          setProduct((p) => ({ ...p, image, additionalImages }))
        }
      />
    </Container>
  );
};

export default EditBrandProductForm;
